/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { TChannel } from "@tivocorp/data-domain-lib";

import { MetadataState, PlaybackContentType } from "../enums";
import { MetadataType } from "../enums";
import { PlaybackError } from "../error";
import type {
  IMetadata,
  IMetadataManager,
  IMetadataModel,
  INdvrPlaybackModel,
  IPlayable
} from "../interfaces";
import type { ISubscription } from "../ISubscription";
import { SubscriptionModel } from "../SubscriptionModel";
import { Log } from "../../logging/Log";
import {
  LinearMetadataModelImpl,
  NdvrMetadataModelImpl,
  SocuMetadataModelImpl,
  VodMetadataModelImpl
} from "../impls";

const EMPTY_METADATA: IMetadata = {};
const EMPTY_PLAYBACK_ERROR: PlaybackError = new PlaybackError("", "");

/**
 * Skeleton MetadataManager implementing IMetadataManager.
 * Orchestrates metadata loading across content types (LIVE, NDVR, SOCU, VOD).
 * Full implementation deferred to Phase 4/7.
 */
export class MetadataManager implements IMetadataManager {
  private static readonly TAG: string = "MetadataManager";

  private mMetadataModel: IMetadataModel | null = null;

  private mMetadataState: MetadataState = MetadataState.NONE;

  private mSubscription: SubscriptionModel<MetadataState> =
    new SubscriptionModel<MetadataState>();

  private mMetadata: IMetadata = {};

  private mError: PlaybackError = EMPTY_PLAYBACK_ERROR;

  private async prepareLiveMetadataByType(
    metadataType: MetadataType,
    channel: TChannel,
    playerCurrentTime?: number
  ): Promise<void> {
    if (metadataType === MetadataType.ACTION_LIST) {
      await this.retrieveActionList(channel);
    } else {
      await this.prepareLiveMetadata(channel, playerCurrentTime);
    }
  }

  private async prepareLiveMetadata(
    channel: TChannel,
    playerCurrentTime?: number
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.AIRING,
        channel,
        playerCurrentTime
      });
      state = MetadataState.AIRING_READY;
    } catch (error) {
      state = MetadataState.AIRING_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
    if (state === MetadataState.AIRING_ERROR) return;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.PREVIEW,
        channel,
        playerCurrentTime
      });
      state = MetadataState.PREVIEW_READY;
    } catch (error) {
      state = MetadataState.PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.ACTION_LIST,
        channel,
        playerCurrentTime
      });
      state = MetadataState.ACTION_LIST_READY;
    } catch (error) {
      state = MetadataState.ACTION_LIST_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async prepareSocuMetadata(channel: TChannel, offerId: string) {
    await this.retrieveActionList(channel, offerId);
    if (this.getMetadataState() === MetadataState.ACTION_LIST_ERROR) return;
    await this.retrieveActionDetails(channel);
    if (this.getMetadataState() === MetadataState.ACTION_DETAILS_ERROR) return;
    await this.retrieveSocuPreview(channel, offerId);
  }

  private async prepareSocuMetadataByType(
    metadataType: MetadataType,
    channel: TChannel,
    offerId: string
  ): Promise<void> {
    switch (metadataType) {
      case MetadataType.PREVIEW:
        await this.retrieveSocuPreview(channel, offerId);
        break;
      case MetadataType.FULL_INFO_PREVIEW:
        await this.retrieveSocuFullPreview(channel);
        break;
      case MetadataType.ACTION_DETAILS:
        await this.retrieveActionDetails(channel);
        break;
      case MetadataType.ACTION_LIST:
        await this.retrieveActionList(channel, offerId);
        break;
      default:
        await this.retrieveActionList(channel, offerId);
    }
  }

  private async retrieveActionList(
    channel: TChannel,
    offerId?: string
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.ACTION_LIST,
        channel,
        offerId
      });
      state = MetadataState.ACTION_LIST_READY;
    } catch (error) {
      state = MetadataState.ACTION_LIST_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveActionDetails(channel: TChannel): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.ACTION_DETAILS,
        channel
      });
      state = MetadataState.ACTION_DETAILS_READY;
    } catch (error) {
      state = MetadataState.ACTION_DETAILS_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveSocuFullPreview(channel: TChannel): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.FULL_INFO_PREVIEW,
        channel
      });
      state = MetadataState.VOD_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.VOD_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveSocuPreview(
    channel: TChannel,
    offerId: string
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.PREVIEW,
        channel,
        offerId
      });
      state = MetadataState.VOD_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.VOD_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async prepareVodMetadata(playable: IPlayable): Promise<void> {
    await this.retrieveVodActionDetails(playable);
    if (this.getMetadataState() === MetadataState.ACTION_DETAILS_ERROR) return;
    await this.retrieveVodActionList();
    await this.retrieveVodPreview();
  }

  private async prepareVodMetadataByType(
    metadataType: MetadataType,
    playable: IPlayable
  ): Promise<void> {
    switch (metadataType) {
      case MetadataType.VOD_ACTION_LIST:
        await this.retrieveVodActionList();
        break;
      case MetadataType.PREVIEW:
        await this.retrieveVodPreview();
        break;
      case MetadataType.FULL_INFO_PREVIEW:
        await this.retrieveVodFullPreview();
        break;
      default:
        await this.retrieveVodActionDetails(playable);
    }
  }

  private async retrieveVodActionDetails(playable: IPlayable): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.ACTION_DETAILS,
        playable
      });
      state = MetadataState.ACTION_DETAILS_READY;
    } catch (error) {
      state = MetadataState.ACTION_DETAILS_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveVodActionList(): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.VOD_ACTION_LIST
      });
      state = MetadataState.VOD_ACTION_READY;
    } catch (error) {
      state = MetadataState.VOD_ACTION_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveVodPreview(): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.PREVIEW
      });
      state = MetadataState.VOD_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.VOD_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveVodFullPreview(): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.FULL_INFO_PREVIEW
      });
      state = MetadataState.VOD_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.VOD_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async prepareNdvrMetadata(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    await this.retrieveNdvrActionList(ndvrPlaybackModel);
    if (this.getMetadataState() === MetadataState.ACTION_LIST_ERROR) return;
    this.retrieveNdvrPreview(ndvrPlaybackModel);
    // The resume point has to be known before the player loads: the recording
    // action is what sets the player up (RECORDING_ACTION_READY), and the
    // load that follows starts at the last viewed position. Fetched first, so
    // the load never races it. A failed fetch only leaves it at 0 (start from
    // the beginning), so it never holds playback up beyond the request itself.
    await this.retrieveNdvrLastViewedPosition(ndvrPlaybackModel);
    this.retrieveNdvrRecordingActionList(ndvrPlaybackModel);
  }

  private async prepareNdvrMetadataByType(
    metadataType: MetadataType,
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    switch (metadataType) {
      case MetadataType.FULL_INFO_PREVIEW:
        await this.retrieveNdvrFullPreview(ndvrPlaybackModel);
        break;
      case MetadataType.NDVR_LAST_VIEWED_POSITION:
        await this.retrieveNdvrLastViewedPosition(ndvrPlaybackModel);
        break;
      case MetadataType.PREVIEW:
        await this.retrieveNdvrPreview(ndvrPlaybackModel);
        break;
      case MetadataType.RECORDING_ACTION_LIST:
        await this.retrieveNdvrRecordingActionList(ndvrPlaybackModel);
        break;
      default:
        await this.retrieveNdvrActionList(ndvrPlaybackModel);
    }
  }

  private async retrieveNdvrActionList(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.ACTION_LIST,
        ndvrPlaybackModel
      });
      state = MetadataState.ACTION_LIST_READY;
    } catch (error) {
      state = MetadataState.ACTION_LIST_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveNdvrRecordingActionList(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.RECORDING_ACTION_LIST,
        ndvrPlaybackModel
      });
      state = MetadataState.RECORDING_ACTION_READY;
    } catch (error) {
      state = MetadataState.RECORDING_ACTION_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveNdvrPreview(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.PREVIEW,
        ndvrPlaybackModel
      });
      state = MetadataState.RECORDING_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.RECORDING_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveNdvrFullPreview(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.FULL_INFO_PREVIEW,
        ndvrPlaybackModel
      });
      state = MetadataState.RECORDING_PREVIEW_READY;
    } catch (error) {
      state = MetadataState.RECORDING_PREVIEW_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private async retrieveNdvrLastViewedPosition(
    ndvrPlaybackModel: INdvrPlaybackModel
  ): Promise<void> {
    if (!this.mMetadataModel) return;
    let state = MetadataState.PREPARING;
    try {
      await this.mMetadataModel.prepareMetadataByType({
        metadataType: MetadataType.NDVR_LAST_VIEWED_POSITION,
        ndvrPlaybackModel
      });
      state = MetadataState.NDVR_LAST_VIEWED_POSITION_READY;
    } catch (error) {
      state = MetadataState.NDVR_LAST_VIEWED_POSITION_ERROR;
      this.mError = this.mMetadataModel.getError();
      Log.e(MetadataManager.TAG, error as string);
    } finally {
      this.setMetadataState(state);
    }
  }

  private ensureMetadataModel(playable: IPlayable): void {
    if (this.mMetadataModel !== null) {
      return;
    }

    if (playable.playbackContentType === PlaybackContentType.LIVE) {
      this.mMetadataModel = new LinearMetadataModelImpl();
      return;
    }

    if (
      playable.playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
      playable.playbackContentType === PlaybackContentType.CATCHUP_RESUME ||
      playable.playbackContentType ===
        PlaybackContentType.STARTOVER_BEGINNING ||
      playable.playbackContentType === PlaybackContentType.STARTOVER_RESUME
    ) {
      this.mMetadataModel = new SocuMetadataModelImpl();
      return;
    }

    if (
      playable.playbackContentType === PlaybackContentType.NDVR_LIVE ||
      playable.playbackContentType === PlaybackContentType.NDVR_RECORDED
    ) {
      this.mMetadataModel = new NdvrMetadataModelImpl();
      return;
    }

    if (playable.playbackContentType === PlaybackContentType.VOD) {
      this.mMetadataModel = new VodMetadataModelImpl();
    }
  }

  private setMetadataState(metadataState: MetadataState) {
    this.mMetadataState = metadataState;
    this.mSubscription.notify(metadataState);
  }

  // ── IMetadataManager ──────────────────────────────────────────────────────

  public getMetadata(): IMetadata {
    return this.mMetadataModel !== null
      ? this.mMetadataModel.getMetadata()
      : EMPTY_METADATA;
  }

  public getMetadataError(): PlaybackError {
    return this.mError;
  }

  public getMetadataState(): MetadataState {
    return this.mMetadataState;
  }

  public getMetadataStateChangeListener(): ISubscription<MetadataState> {
    return this.mSubscription;
  }

  public prepareMetadata(playable: IPlayable): void {
    Log.i(
      MetadataManager.TAG,
      "prepareMetadata() - stub: " + playable.playbackContentType
    );
    this.mError = EMPTY_PLAYBACK_ERROR;
    // Reset directly (not via setMetadataState()) so no spurious NONE
    // notification fires - only avoids a stale *_ERROR from the previous
    // tune winning the setUpPlayer() race in continueStartPlayback().
    this.mMetadataState = MetadataState.NONE;
    const playbackContentType = playable.playbackContentType;
    if (playbackContentType === PlaybackContentType.LIVE) {
      if (!playable.channel) return;
      this.mMetadataModel = new LinearMetadataModelImpl();
      this.prepareLiveMetadata(playable.channel).catch((error: unknown) => {
        Log.e(MetadataManager.TAG, error as string);
      });
    } else if (
      playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
      playbackContentType === PlaybackContentType.CATCHUP_RESUME ||
      playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
      playbackContentType === PlaybackContentType.STARTOVER_RESUME
    ) {
      if (!playable.channel) return;
      this.mMetadataModel = new SocuMetadataModelImpl();
      this.prepareSocuMetadata(playable.channel, playable.offerId || "").catch(
        (error: unknown) => {
          Log.e(MetadataManager.TAG, error as string);
        }
      );
    } else if (
      playbackContentType === PlaybackContentType.NDVR_LIVE ||
      playbackContentType === PlaybackContentType.NDVR_RECORDED
    ) {
      if (!playable.ndvrPlaybackModel) return;
      this.mMetadataModel = new NdvrMetadataModelImpl();
      this.prepareNdvrMetadata(playable.ndvrPlaybackModel).catch(
        (error: unknown) => {
          Log.e(MetadataManager.TAG, error as string);
        }
      );
    } else if (playbackContentType === PlaybackContentType.VOD) {
      if (!playable.vodPlaybackModel) return;
      this.mMetadataModel = new VodMetadataModelImpl();
      this.prepareVodMetadata(playable).catch((error: unknown) => {
        Log.e(MetadataManager.TAG, error as string);
      });
    }
  }
  public prepareMetadataByType(
    playable: IPlayable,
    metadataType: MetadataType,
    _playerCurrentTime?: number
  ): void {
    Log.i(
      MetadataManager.TAG,
      "prepareMetadataByType() - stub: " +
        playable.playbackContentType +
        " type=" +
        metadataType
    );
    this.ensureMetadataModel(playable);
    const playbackContentType = playable.playbackContentType;
    if (playbackContentType === PlaybackContentType.LIVE) {
      if (!playable.channel) return;
      this.prepareLiveMetadataByType(
        metadataType,
        playable.channel,
        _playerCurrentTime
      ).catch((error: unknown) => {
        Log.e(MetadataManager.TAG, error as string);
      });
    } else if (
      playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
      playbackContentType === PlaybackContentType.CATCHUP_RESUME ||
      playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
      playbackContentType === PlaybackContentType.STARTOVER_RESUME
    ) {
      if (!playable.channel) return;
      this.prepareSocuMetadataByType(
        metadataType,
        playable.channel,
        playable.offerId || ""
      ).catch((error: unknown) => {
        Log.e(MetadataManager.TAG, error as string);
      });
    } else if (
      playbackContentType === PlaybackContentType.NDVR_LIVE ||
      playbackContentType === PlaybackContentType.NDVR_RECORDED
    ) {
      if (!playable.ndvrPlaybackModel) return;
      this.prepareNdvrMetadataByType(
        metadataType,
        playable.ndvrPlaybackModel
      ).catch((error: unknown) => {
        Log.e(MetadataManager.TAG, error as string);
      });
    } else if (playbackContentType === PlaybackContentType.VOD) {
      if (!playable.vodPlaybackModel) return;
      this.prepareVodMetadataByType(metadataType, playable).catch(
        (error: unknown) => {
          Log.e(MetadataManager.TAG, error as string);
        }
      );
    }
  }
}
